import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  attentionForAge,
  attentionLabel,
  cogginsDocumentStatus,
  COGGINS_DUE_DAYS,
  farrierAttention,
  formatReportDate,
  RABIES_DUE_DAYS,
  WORMER_DUE_DAYS
} from './horseReportUtils.ts';

export type HorseReportPdfTab = 'medical' | 'farrier' | 'informational';

export interface HorseReportPdfHorse {
  name: string;
  stallNumber?: string;
  designation: string;
  status: string;
  farrierName?: string;
  lastSeenByFarrier?: string;
  lastCoggins?: string;
  lastWormer?: string;
  lastRabies?: string;
  cogginsDocumentUrl?: string;
  cogginsDocumentOriginalName?: string;
  specialNeeds?: string;
  medicalNotes?: string;
  notes?: string;
  temperament?: string;
  ridingLevel?: string;
  color?: string;
  ownerFamily?: { familyName: string };
  assignedRanchGirls?: Array<{ firstName: string; lastName: string }>;
}

const tabTitles: Record<HorseReportPdfTab, string> = {
  medical: 'Medical Report',
  farrier: 'Farrier Report',
  informational: 'Informational Report'
};

function truncate(text: string | undefined, max = 120): string {
  if (!text) return '—';
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function assignedLabel(horse: HorseReportPdfHorse): string {
  if (!horse.assignedRanchGirls?.length) return 'Not assigned';
  return horse.assignedRanchGirls.map(g => `${g.firstName} ${g.lastName}`).join(', ');
}

function ownerLabel(horse: HorseReportPdfHorse): string {
  if (horse.designation === 'personal horse' && horse.ownerFamily) {
    return horse.ownerFamily.familyName;
  }
  return 'GGAA';
}

function addReportHeader(doc: jsPDF, tab: HorseReportPdfTab, horseCount: number, needsAttentionOnly: boolean) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text("God's Green Acre Association", pageWidth / 2, 40, { align: 'center' });
  doc.setFontSize(13);
  doc.text(`Horse ${tabTitles[tab]}`, pageWidth / 2, 58, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const filterNote =
    tab === 'informational'
      ? 'All active horses'
      : needsAttentionOnly
        ? 'Horses needing attention only'
        : 'All active horses';
  doc.text(`Generated ${new Date().toLocaleString()} · ${horseCount} horse(s) · ${filterNote}`, pageWidth / 2, 72, {
    align: 'center'
  });
}

export function exportHorseReportPdf(
  tab: HorseReportPdfTab,
  horses: HorseReportPdfHorse[],
  options: { needsAttentionOnly: boolean }
) {
  if (horses.length === 0) {
    window.alert('No horses to export for this report.');
    return;
  }

  const orientation = tab === 'medical' || tab === 'informational' ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'letter' });
  addReportHeader(doc, tab, horses.length, options.needsAttentionOnly);

  const startY = 88;

  if (tab === 'medical') {
    autoTable(doc, {
      startY,
      head: [[
        'Horse',
        'Stall',
        'Last rabies',
        'Rabies status',
        'Last wormer',
        'Wormer status',
        'Last Coggins',
        'Coggins status',
        'Coggins document',
        'Special needs',
        'Medical notes'
      ]],
      body: horses.map(h => [
        h.name,
        h.stallNumber || '—',
        formatReportDate(h.lastRabies),
        attentionLabel[attentionForAge(h.lastRabies, RABIES_DUE_DAYS)],
        formatReportDate(h.lastWormer),
        attentionLabel[attentionForAge(h.lastWormer, WORMER_DUE_DAYS)],
        formatReportDate(h.lastCoggins),
        attentionLabel[attentionForAge(h.lastCoggins, COGGINS_DUE_DAYS)],
        h.cogginsDocumentUrl
          ? `${h.cogginsDocumentOriginalName || 'On file'} (${attentionLabel[cogginsDocumentStatus(h)]})`
          : attentionLabel[cogginsDocumentStatus(h)],
        truncate(h.specialNeeds, 80),
        truncate(h.medicalNotes, 80)
      ]),
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [44, 95, 45], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 249, 245] },
      margin: { left: 28, right: 28 },
      didDrawPage: data => {
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 20,
          { align: 'center' }
        );
      }
    });
  } else if (tab === 'farrier') {
    autoTable(doc, {
      startY,
      head: [['Horse', 'Stall', 'Designation', 'Status', 'Farrier', 'Last seen', 'Attention']],
      body: horses.map(h => [
        h.name,
        h.stallNumber || '—',
        h.designation,
        h.status,
        h.farrierName || '—',
        formatReportDate(h.lastSeenByFarrier),
        attentionLabel[farrierAttention(h.lastSeenByFarrier)]
      ]),
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [44, 95, 45], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 249, 245] },
      margin: { left: 40, right: 40 },
      didDrawPage: data => {
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 20,
          { align: 'center' }
        );
      }
    });
  } else {
    autoTable(doc, {
      startY,
      head: [[
        'Horse',
        'Stall',
        'Designation',
        'Status',
        'Color',
        'Temperament',
        'Riding level',
        'Owner',
        'Assigned to',
        'Special needs',
        'Notes'
      ]],
      body: horses.map(h => [
        h.name,
        h.stallNumber || '—',
        h.designation,
        h.status,
        h.color || '—',
        h.temperament || '—',
        h.ridingLevel || '—',
        ownerLabel(h),
        assignedLabel(h),
        truncate(h.specialNeeds, 70),
        truncate(h.notes, 70)
      ]),
      styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [44, 95, 45], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 249, 245] },
      margin: { left: 28, right: 28 },
      didDrawPage: data => {
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 20,
          { align: 'center' }
        );
      }
    });
  }

  const dateStamp = new Date().toISOString().split('T')[0];
  doc.save(`GGAA_Horse_${tab}_Report_${dateStamp}.pdf`);
}
